/**
 * Spanish ID validators (NIF, NIE, CIF).
 * Frontend validation matching backend logic.
 */

// Valid EU country codes for VAT (including XI for Northern Ireland)
const EU_VAT_COUNTRY_CODES = [
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "EL",
  "ES",
  "FI",
  "FR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  "XI",
];

// EU country codes for country selection (excluding Spain - has its own category)
// Also includes GR as alias for EL (Greece)
export const EU_COUNTRY_CODES = [
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "FI",
  "FR",
  "GR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
];

/**
 * Check if a country code is in the EU (excluding Spain).
 */
export function isEUCountry(countryCode: string | null | undefined) {
  if (!countryCode) return false;
  const code = countryCode.toUpperCase();
  // Handle Greece alternate code 'EL'
  if (code === "EL") return true;
  return EU_COUNTRY_CODES.includes(code);
}

/**
 * Check if a country code is outside the EU.
 */
export function isNonEUCountry(countryCode: string | null | undefined) {
  if (!countryCode) return false;
  const code = countryCode.toUpperCase();
  // Spain is EU but has its own category
  if (code === "ES") return false;
  return !isEUCountry(code);
}

const NIF_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";
const NIE_PREFIX_VALUES: Record<string, string> = { X: "0", Y: "1", Z: "2" };
const CIF_LETTER_CONTROL_TYPES = "KPQRSW";
const CIF_CONTROL_LETTERS = "JABCDEFGHI";
const CIF_VALID_FIRST_LETTERS = "ABCDEFGHJKLMNPQRSUVW";

/**
 * Normalize a Spanish ID by removing spaces, dashes, and converting to uppercase.
 */
function normalizeSpanishId(id: string) {
  return id.replace(/[\s\-.]/g, "").toUpperCase();
}

/**
 * Validate a Spanish NIF (8 digits + 1 check letter).
 */
function validateNIF(nif: string) {
  const normalized = normalizeSpanishId(nif);
  const match = normalized.match(/^(\d{8})([A-Z])$/);

  if (!match) {
    return { valid: false, error: "NIF must be 8 digits followed by a letter" };
  }

  const [, digits, letter] = match;
  const expectedLetter = NIF_LETTERS[parseInt(digits, 10) % 23];

  if (letter !== expectedLetter) {
    return { valid: false, error: "Invalid NIF check letter" };
  }

  return { valid: true };
}

/**
 * Validate a Spanish NIE (X/Y/Z + 7 digits + 1 check letter).
 */
function validateNIE(nie: string) {
  const normalized = normalizeSpanishId(nie);
  const match = normalized.match(/^([XYZ])(\d{7})([A-Z])$/);

  if (!match) {
    return { valid: false, error: "NIE must be X/Y/Z followed by 7 digits and a letter" };
  }

  const [, prefix, digits, letter] = match;
  if (!(prefix in NIE_PREFIX_VALUES)) {
    return { valid: false, error: "Invalid NIE prefix" };
  }
  const number = parseInt(NIE_PREFIX_VALUES[prefix] + digits, 10);
  const expectedLetter = NIF_LETTERS[number % 23];

  if (letter !== expectedLetter) {
    return { valid: false, error: "Invalid NIE check letter" };
  }

  return { valid: true };
}

/**
 * Validate a Spanish CIF (1 letter + 7 digits + 1 control character).
 */
function validateCIF(cif: string) {
  const normalized = normalizeSpanishId(cif);
  const match = normalized.match(/^([A-Z])(\d{7})([A-Z0-9])$/);

  if (!match) {
    return { valid: false, error: "CIF must be 1 letter + 7 digits + 1 control character" };
  }

  const [, firstLetter, digits, control] = match;

  if (!CIF_VALID_FIRST_LETTERS.includes(firstLetter)) {
    return { valid: false, error: `Invalid CIF first letter: ${firstLetter}` };
  }

  let sumA = 0;
  let sumB = 0;

  for (let i = 0; i < 7; i++) {
    const digit = parseInt(digits[i], 10);
    if (i % 2 === 0) {
      const doubled = digit * 2;
      sumA += doubled > 9 ? doubled - 9 : doubled;
    } else {
      sumB += digit;
    }
  }

  const total = (sumA + sumB) % 10;
  const controlDigit = total === 0 ? 0 : 10 - total;
  const controlLetter = CIF_CONTROL_LETTERS[controlDigit];
  const requiresLetter = CIF_LETTER_CONTROL_TYPES.includes(firstLetter);

  if (requiresLetter) {
    if (control !== controlLetter) {
      return { valid: false, error: "Invalid CIF control letter" };
    }
  } else {
    if (control !== String(controlDigit) && control !== controlLetter) {
      return { valid: false, error: "Invalid CIF control character" };
    }
  }

  return { valid: true };
}

/**
 * Validate EU VAT number format.
 * Checks for valid EU country code prefix and alphanumeric format.
 */
export function validateEUVAT(vat: string) {
  const normalized = vat.replace(/[\s\-.]/g, "").toUpperCase();

  // Must be at least 4 characters (2-letter country code + 2 digits/letters)
  if (normalized.length < 4) {
    return { valid: false, error: "EU VAT number is too short" };
  }

  // Extract and validate country code
  const countryCode = normalized.substring(0, 2);
  if (!EU_VAT_COUNTRY_CODES.includes(countryCode)) {
    return {
      valid: false,
      error: `Invalid EU country code: ${countryCode}. Must be a valid EU member state code.`,
    };
  }

  // Check the rest is alphanumeric (2-12 characters)
  const vatNumber = normalized.substring(2);
  if (!/^[A-Z0-9]{2,12}$/.test(vatNumber)) {
    return { valid: false, error: "EU VAT must be country code + 2-12 alphanumeric characters" };
  }

  return { valid: true };
}

/**
 * Detect the type of a Spanish tax ID based on its format.
 * Returns "NIF", "NIE", "CIF", or null if unrecognized.
 */
export function detectSpanishTaxIdType(taxId: string | null | undefined) {
  if (!taxId) return null;

  const normalized = taxId.replace(/[\s\-.]/g, "").toUpperCase();

  // Check NIE pattern first (X/Y/Z + 7 digits + letter)
  if (/^[XYZ]\d{7}[A-Z]$/.test(normalized)) {
    const validation = validateNIE(normalized);
    if (validation.valid) return "NIE";
  }

  // Check NIF pattern (8 digits + letter)
  if (/^\d{8}[A-Z]$/.test(normalized)) {
    const validation = validateNIF(normalized);
    if (validation.valid) return "NIF";
  }

  // Check CIF pattern (letter + 7 digits + control)
  if (/^[A-Z]\d{7}[A-Z0-9]$/.test(normalized)) {
    const validation = validateCIF(normalized);
    if (validation.valid) return "CIF";
  }

  return null;
}

/**
 * Validate a tax ID based on its type.
 */
export function validateSpanishTaxId(taxIdNumber: string | null | undefined, taxIdType: string) {
  if (!taxIdNumber) {
    return { valid: false, error: "Tax ID number is required" };
  }

  switch (taxIdType) {
    case "NIF":
      return validateNIF(taxIdNumber);
    case "NIE":
      return validateNIE(taxIdNumber);
    case "CIF":
      return validateCIF(taxIdNumber);
    case "EU_VAT":
      return validateEUVAT(taxIdNumber);
    case "PASSPORT":
    case "OTHER":
      // No validation for passport/other - just check it's not empty
      return { valid: true };
    default:
      return { valid: true };
  }
}

export default { validateSpanishTaxId };
